import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BackIcon from '../../Assets/images/back.svg';
import MenuIcon from '../../Assets/images/menu.svg';
import MembershipSaleSidebarInfo from '../../Pages/Bookings/MembershipSaleSidebarInfo';
import { MembershipTxnPaymentDetailSheet } from '../MembershipTransactionSidePanel/MembershipTransactionSidePanel';
import styles from './MembershipSaleSidePanel.module.css';

/**
 * Sales-style membership top-up sidebar (same content as /sales membership sale).
 * panelType: "sales" (default) | "membershipCredit" (payment row actions in child).
 */
const MembershipSaleSidePanel = ({
    onClose,
    title,
    detail,
    saleNote,
    onAddNote,
    panelType = 'sales',
    menuOptions = [],
}) => {
    const navigate = useNavigate();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const menuRef = useRef(null);
    const [isPaymentDetailOpen, setIsPaymentDetailOpen] = useState(false);
    const [selectedPayment, setSelectedPayment] = useState(null);

    const detailKey = detail?.id ?? null;

    useEffect(() => {
        setIsPaymentDetailOpen(false);
        setSelectedPayment(null);
    }, [detailKey]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setIsMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const resolvedNote = String(saleNote ?? detail?.sale_note ?? '').trim();
    const normalizedMenuOptions = Array.isArray(menuOptions) ? menuOptions : [];
    const showHeaderMenu = typeof onAddNote === 'function' || normalizedMenuOptions.length > 0;
const isPartialPayment =
    String(detail?.payment_status || '').toLowerCase() === 'partial';

const handleAddPayment = useCallback(() => {
    const saleId = detail?.sale_id ?? detail?.id;
    if (!saleId) return;
    onClose();
    navigate(`/sales?search=${saleId}`);
}, [detail?.sale_id, detail?.id, onClose, navigate]);

    return (
        <div className={styles.overlay} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} role="presentation">
            <aside
                className={styles.panel}
                onClick={(event) => event.stopPropagation()}
                aria-label="Membership sale details"
            >
                <header className={styles.header}>
                    <button type="button" className={styles.backButton} onClick={onClose} aria-label="Back">
                        <img src={BackIcon} alt="" />
                    </button>
                    <h2 className={styles.title}>{title}</h2>
                    {showHeaderMenu ? (
                        <div className={styles.menuWrapper} ref={menuRef}>
                                <button
                                    type="button"
                                    className={styles.menuTrigger}
                                    onClick={() => setIsMenuOpen((prev) => !prev)}
                                    aria-label="More options"
                                >
                                    <img src={MenuIcon} alt="" />
                                </button>
                                {isMenuOpen ? (
                                    <ul className={styles.menuDropdown}>
                                        {typeof onAddNote === 'function' ? (
                                            <li
                                                className={styles.menuItem}
                                                onClick={() => {
                                                    setIsMenuOpen(false);
                                                    onAddNote();
                                                }}
                                            >
                                                {resolvedNote ? 'Edit Notes' : 'Add Notes'}
                                            </li>
                                        ) : null}
                                        {normalizedMenuOptions.map((option) => (
                                            <li
                                                key={option.label}
                                                className={`${styles.menuItem} ${option.disabled ? styles.menuItemDisabled : ''}`}
                                                title={option.tooltip || ''}
                                                onClick={() => {
                                                    if (option.disabled) return;
                                                    setIsMenuOpen(false);
                                                    if (typeof option.action === 'function') {
                                                        option.action();
                                                    }
                                                }}
                                            >
                                                {option.label}
                                            </li>
                                        ))}
                                    </ul>
                                ) : null}
                            </div>
                        ) : null}
                </header>
                <div className={styles.body}>
                    <MembershipSaleSidebarInfo
                        detail={detail}
                        saleNote={saleNote}
                        showAddNotePopup={onAddNote}
                        panelType={panelType}
                        onPaymentViewDetails={(payment) => {
                            setSelectedPayment(payment);
                            setIsPaymentDetailOpen(true);
                        }}
                        onAddPayment={isPartialPayment ? handleAddPayment : undefined}
                    />
                </div>
            </aside>
            {isPaymentDetailOpen && detail ? (
                <>
                    <div className={styles.nestedBackdrop} aria-hidden="true" />
                    <MembershipTxnPaymentDetailSheet
                        detail={detail}
                        selectedPayment={selectedPayment}
                        allPayments={detail?.payments}
                        refundDate={selectedPayment?.updated_at ?? detail?.membership_transaction?.updated_at ?? detail?.updated_at}
                        onClose={() => {
                            setIsPaymentDetailOpen(false);
                            setSelectedPayment(null);
                        }}
                    />
                </>
            ) : null}
        </div>
    );
};

export default MembershipSaleSidePanel;
